import { useMemo, useState } from 'react';
import {
  PreflightCopy,
  SettlementAgingThresholdBounds,
  ErrorCode,
} from '@signalvote/shared';
import type { PaymentProcessorListItemDto } from '@signalvote/shared';
import { usePaymentProcessors } from '@/hooks/queries/usePaymentProcessorRate';
import {
  useProcessorThresholdImpact,
  useUpdateProcessorThreshold,
  UpdateProcessorThresholdError,
} from '@/hooks/queries/usePaymentProcessorThreshold';
import { CANONICAL_PROCESSOR_RATES } from './canonicalProcessorRates';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// USI-003 / ADR-011: rate-edit row was removed (the FPD-051 fee rollup math
// the gate existed to protect was deleted in USI-006). The page now hosts
// only the FPD-022 per-processor settlement-aging threshold editor.

export function ProcessorRateSettingsPage() {
  const { data: processors, isLoading } = usePaymentProcessors();

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!processors || processors.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No payment processors configured. Add one in{' '}
        <a href="/admin/finance/settings/setup" className="underline text-foreground">
          Setup
        </a>
        .
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Processor settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Tune the settlement-aging threshold per processor — the number of days
          a contribution can stay unsettled before it surfaces as aged-overdue.
        </p>
      </header>

      <div className="space-y-3">
        {processors.map((p) => (
          <ProcessorRow key={p.id} processor={p} />
        ))}
      </div>
    </div>
  );
}

interface ProcessorRowProps {
  processor: PaymentProcessorListItemDto;
}

function ProcessorRow({ processor }: ProcessorRowProps) {
  const canonical = CANONICAL_PROCESSOR_RATES[processor.kind];
  const processorName = processor.displayName?.split(' — ')[0] ?? canonical.displayName;

  return (
    <div
      className="rounded-md border border-border p-4 space-y-3"
      data-testid={`processor-row-${processor.kind}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{processor.displayName ?? processor.kind}</div>
          <div className="text-xs text-muted-foreground">{processor.kind}</div>
        </div>
      </div>

      <ProcessorThresholdSection processor={processor} processorName={processorName} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FPD-022 — settlement-aging threshold section.
// ---------------------------------------------------------------------------

interface ProcessorThresholdSectionProps {
  processor: PaymentProcessorListItemDto;
  processorName: string;
}

function ProcessorThresholdSection({
  processor,
  processorName,
}: ProcessorThresholdSectionProps) {
  const [editing, setEditing] = useState(false);
  const { data: impact } = useProcessorThresholdImpact(processor.id);

  const currentLabel = processor.settlementAgingThresholdDays != null
    ? `${processor.settlementAgingThresholdDays} days`
    : PreflightCopy.ProcessorThresholdDefaultLabel
        .replace('{defaultDays}', String(SettlementAgingThresholdBounds.Default));

  const impactText = useMemo(() => {
    if (!impact || impact.inFlightContributionCount === 0) return null;
    return PreflightCopy.ProcessorThresholdImpactSubtitle
      .replace('{N}', String(impact.inFlightContributionCount));
  }, [impact]);

  return (
    <div
      className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2"
      data-testid={`processor-threshold-section-${processor.kind}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {PreflightCopy.ProcessorSettingsSectionTitle}
          </div>
          <div className="text-sm text-foreground">
            {currentLabel}
            {processor.settlementAgingThresholdNotes && (
              <span className="ml-2 text-xs italic text-muted-foreground">
                — {processor.settlementAgingThresholdNotes}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {PreflightCopy.ProcessorSettingsSectionSubtitle
              .replace('{processorName}', processorName)}
          </div>
          {impactText && (
            <div
              className="text-xs text-amber-600"
              data-testid={`processor-threshold-impact-${processor.kind}`}
            >
              {impactText}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setEditing(true)}
          data-testid={`processor-threshold-edit-${processor.kind}`}
        >
          Edit
        </Button>
      </div>
      {editing && (
        <ProcessorThresholdEditModal
          processor={processor}
          processorName={processorName}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

interface ProcessorThresholdEditModalProps {
  processor: PaymentProcessorListItemDto;
  processorName: string;
  onClose: () => void;
}

function ProcessorThresholdEditModal({
  processor,
  processorName,
  onClose,
}: ProcessorThresholdEditModalProps) {
  const [thresholdInput, setThresholdInput] = useState(
    processor.settlementAgingThresholdDays != null
      ? String(processor.settlementAgingThresholdDays)
      : '',
  );
  const [notesInput, setNotesInput] = useState(
    processor.settlementAgingThresholdNotes ?? '',
  );
  const [resetToDefault, setResetToDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useUpdateProcessorThreshold();

  const prompt = PreflightCopy.ProcessorThresholdEditPrompt
    .replace('{processorName}', processorName);

  const submit = async () => {
    setError(null);
    let thresholdDays: number | null;
    if (resetToDefault) {
      thresholdDays = null;
    } else {
      const parsed = parseInt(thresholdInput, 10);
      if (Number.isNaN(parsed)
        || parsed < SettlementAgingThresholdBounds.Min
        || parsed > SettlementAgingThresholdBounds.Max) {
        setError(PreflightCopy.ProcessorThresholdInvalidRange);
        return;
      }
      thresholdDays = parsed;
    }
    const trimmedNotes = notesInput.trim();
    try {
      await mutateAsync({
        processorId: processor.id,
        body: {
          thresholdDays,
          notes: trimmedNotes.length === 0 ? null : trimmedNotes,
        },
      });
      onClose();
    } catch (e) {
      if (e instanceof UpdateProcessorThresholdError) {
        if (e.code === ErrorCode.PaymentProcessorThresholdInvalidRange) {
          setError(PreflightCopy.ProcessorThresholdInvalidRange);
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : 'Failed to update threshold');
      }
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="processor-threshold-edit-modal"
      >
        <DialogHeader>
          <DialogTitle>{prompt}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div
            className={cn(
              'flex items-center gap-3',
              resetToDefault && 'opacity-50',
            )}
          >
            <Label htmlFor={`threshold-input-${processor.id}`} className="text-xs">
              Days
            </Label>
            <Input
              id={`threshold-input-${processor.id}`}
              type="number"
              min={SettlementAgingThresholdBounds.Min}
              max={SettlementAgingThresholdBounds.Max}
              value={thresholdInput}
              onChange={(e) => {
                setThresholdInput(e.target.value);
                setResetToDefault(false);
              }}
              className="w-24"
              disabled={resetToDefault}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={resetToDefault}
              onChange={(e) => setResetToDefault(e.target.checked)}
              data-testid={`threshold-reset-to-default-${processor.kind}`}
            />
            {PreflightCopy.ProcessorThresholdResetToDefaultLabel}
          </label>
          <div className="space-y-1">
            <Label htmlFor={`threshold-notes-${processor.id}`} className="text-xs">
              {PreflightCopy.ProcessorThresholdNotesLabel}
            </Label>
            <Input
              id={`threshold-notes-${processor.id}`}
              type="text"
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              placeholder="e.g. payouts run on a 30-day cycle"
            />
          </div>
          {error && (
            <div
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={isPending}
            data-testid="processor-threshold-save"
          >
            {isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
